import { useCallback, useEffect, useState } from 'react';

import { getModulePresets } from '../utils/helpers/get-module-presets';
import { importModule } from '../utils/helpers/import-module';
import { MultiVariantModuleConfig, PayloadsType, VariantType } from '../utils/types';

/**
 * Custom hook to get the feature flag module.
 *
 * @param featureFlagName The name of the feature flag.
 * @param singleVariant Whether to use the single variant or multiple variant feature flag module.
 * @returns An object containing the payloads and bootstrapVariant of the feature flag module.
 */
export const useGetFeatureFlagModule = <T = boolean>(featureFlagName: string) => {
  const [loadedModule, setLoadedModule] = useState<MultiVariantModuleConfig<T>>();
  const [isSettled, setIsSettled] = useState(false);

  const loadModule = useCallback(async () => {
    try {
      const importedModule = await importModule<T>(featureFlagName);
      const moduleWithPresets = getModulePresets<T>(importedModule);
      setLoadedModule(moduleWithPresets);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Could not setup feature flag ${featureFlagName}`, error);
    } finally {
      // Settled on failure too, so that a module which will never load does not leave callers waiting forever.
      setIsSettled(true);
    }
  }, [featureFlagName]);

  // This useEffect is solely used to import the feature flag module.
  // We could've done it with a useMemo if it wasn't discouraged to use async in that hook.
  useEffect(() => {
    // Import the feature flag module.
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadModule();
  }, [loadModule]);

  return {
    // Return the payloads and bootstrapVariant of the feature flag module.
    payloads: loadedModule?.Payloads as PayloadsType<T>,
    bootstrapVariant: loadedModule?.BootstrapVariant as VariantType,
    isSettled,
  };
};
